import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Tooltip, 
  message, 
  Modal,
  Input, 
  Select, 
  Row,
  Col,
  Avatar,
  Skeleton
} from 'antd';
import customerService from '../../services/customerService';
// import { Icon } from '@iconify/react/dist/iconify.js';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  CheckCircleOutlined, 
  WarningOutlined, 
  StopOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getLicenses, 
  deleteLicense,
  LICENSE_STATUS,
  LICENSE_TYPES
} from '../../services/licenseService';

const { Search } = Input;
const { Option } = Select;

const CustomerCell = ({ customerLicenses }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerLicenses || customerLicenses.length === 0) {
        return;
      }

      const primaryCustomer = customerLicenses.find(cl => cl.is_primary) || customerLicenses[0];
      const customerId = primaryCustomer?.customer_id;

      if (!customerId) {
        setError('No customer ID found');
        return;
      }

      if (primaryCustomer.company_name) {
        setCustomer(primaryCustomer.company_name);
        return;
      }

      setLoading(true);
      try {
        const customerData = await customerService.getCustomer(customerId);
        setCustomer(customerData);
      } catch (err) {
        console.error('Error fetching customer:', err);
        setError('Failed to load customer data');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerLicenses]);

  if (loading) return <Skeleton.Input style={{ width: 150 }} active size="small" />;
  if (error) return <span>Error loading customer</span>;
  if (!customerLicenses || customerLicenses.length === 0) {
    return <span>Unassigned</span>;
  }

  const primaryCustomer = customerLicenses.find(cl => cl.is_primary) || customerLicenses[0];
  
  if (customer) {
    const displayName = customer.company_name || customer.contact_name || 'Unnamed Customer';
    // const logoUrl = customer.logo_url || customer.company_logo;

    const logoUrl = customer.company_logo ? (
      customer.company_logo.startsWith('uploads/') ? 
        `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}/static/${customer.company_logo.replace('uploads/', '')}` :
        customer.company_logo.startsWith('/static/') ?
          `${(process.env.REACT_APP_ROOT_URL || 'http://localhost:8000').replace('/api/v1', '')}${customer.company_logo}` :
          customer.company_logo
    ) : null;
    
    return (
      <Tooltip title={`ID: ${customer.id}${primaryCustomer.is_primary ? ' (Primary)' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {logoUrl && (
            <Avatar 
                src={logoUrl}
                size="small"
                style={{ 
                  backgroundColor: customer.company_logo ? 'transparent' : '#1890ff',
                  border: customer.status === 'active' ? '1px solid #19f705' : '1px solid #fc0202',
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              ></Avatar>
          )}
          <span>{displayName}</span>
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`ID: ${primaryCustomer.customer_id}${primaryCustomer.is_primary ? ' (Primary)' : ''}`}>
      <span>Customer #{primaryCustomer.customer_id}</span>
    </Tooltip>
  );
};

const LicenseList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [licenseToDelete, setLicenseToDelete] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: '',
    is_active: true 
  });

  // Fetch licenses function
  const fetchLicenses = useCallback(async () => {

    console.log("user====", user)
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const { page, limit } = pagination;
      const skip = (page - 1) * limit;
      
      // Make the API call with all filter parameters
      const response = await getLicenses(
        skip, 
        limit, 
        filters.status || undefined, 
        filters.type || undefined, 
        null, // customerId
        null, // expiresSoon
        filters.search || undefined
      );

      
      // Handle the API response
      let items = [];
      let total = 0;
      
      if (response && response.items) {
        // New format: { items: [...], total: number }
        items = response.items || [];
        total = response.total || 0;
      } else if (Array.isArray(response)) {
        // Legacy format: array of items
        items = response;
        total = response.length;
      }
      
      // Update state with the fetched data
      setLicenses(items);
      setPagination(prev => ({
        ...prev,
        total: total,
      }));
      setError(null);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      let errorMessage = 'Failed to load licenses';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        const { status, data } = error.response;
        console.error(`Backend returned status ${status}`, data);
        
        if (status === 401) {
          errorMessage = 'Your session has expired. Please log in again.';
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 1500);
        } else if (status === 403) {
          errorMessage = 'You do not have permission to view licenses.';
        } else if (status === 404) {
          errorMessage = 'No licenses found matching your criteria.';
        } else if (status === 400) {
          // Handle validation errors or bad requests
          errorMessage = data?.detail || 'Invalid request. Please check your input.';
        } else if (status >= 500) {
          errorMessage = 'A server error occurred. Our team has been notified. Please try again later.';
        } else {
          // For other 4xx/5xx errors
          errorMessage = data?.detail || `Error: ${status} - ${data?.message || 'An error occurred'}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received from server', error.request);
        errorMessage = 'Unable to connect to the server. Please check your network connection and try again.';
      } else if (error.message === 'Network Error') {
        // Network error (e.g., CORS, offline)
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message && error.message.includes('timeout')) {
        // Request timeout
        errorMessage = 'Request timed out. The server is taking too long to respond. Please try again.';
      }
      
      // Set the error state and show toast
      setError(errorMessage);
      toast.error(errorMessage, {
        autoClose: 5000,
        position: 'top-right',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, pagination.page, pagination.limit, JSON.stringify(filters)]);


  // Fetch licenses when component mounts or when fetchLicenses changes
  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);



  const handleTableChange = (pagination, filters, sorter) => {
    setPagination({
      ...pagination,
      page: pagination.current,
      limit: pagination.pageSize,
    });
  };

  const handleDelete = async () => {
    if (!licenseToDelete) {
      console.error('No license ID provided for deletion');
      message.error('No license selected for deletion');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Initiating license deletion for ID:', licenseToDelete);
      
      const response = await deleteLicense(licenseToDelete);
      
      // Handle successful response
      if (response && response.success) {
        const successMessage = response.message || 'License deactivated successfully';
        console.log('License deletion successful:', { response, licenseId: licenseToDelete });
        message.success(successMessage);
        
        // Reset state and refresh the license list
        setShowDeleteModal(false);
        setLicenseToDelete(null);
        await fetchLicenses();
        
        return; // Exit early on success
      }
      
      // Handle unexpected but non-error response
      const errorMessage = response?.message || 'Failed to deactivate license (unexpected response)';
      message.error(errorMessage);
      
    } catch (error) {
      // Handle different error status codes
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { status, data } = error.response;
        console.error(`Backend returned status ${status}`, data);
        
        if (status === 401) {
          // Session expired or not logged in
          message.error('Your session has expired. Please log in again.');
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login');
          }, 1500);
          return;
        } else if (status === 403) {
          // Permission denied
          message.error('You do not have permission to delete licenses. Only administrators can perform this action.');
        } else if (status === 404) {
          // License not found
          message.error('The specified license was not found or has already been deleted.');
        } else if (status === 400) {
          // Bad request (e.g., active customers)
          const errorMessage = data?.detail || 'Cannot delete license. It may have active customer assignments.';
          message.error(errorMessage);
        } else if (status >= 500) {
          // Server error
          message.error('A server error occurred while deleting the license. Our team has been notified. Please try again later.');
        } else {
          // Other 4xx/5xx errors
          const errorMessage = data?.detail || data?.message || 'An unexpected error occurred';
          message.error(`Error: ${errorMessage}`);
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received from server', error.request);
        message.error('Unable to connect to the server. Please check your network connection and try again.');
      } else if (error.message === 'Network Error') {
        // Network error (e.g., CORS, offline)
        message.error('Network error. Please check your internet connection and try again.');
      } else if (error.message && error.message.includes('timeout')) {
        // Request timeout
        message.error('Request timed out. The server is taking too long to respond. Please try again.');
      } else {
        // Other errors (e.g., errors in the code)
        console.error('Unexpected error:', error);
        message.error({
          content: error.message || 'An unexpected error occurred',
          duration: 5,
          style: { whiteSpace: 'pre-line' }
        });
      }
      
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setFilters(prev => ({
      ...prev,
      search: value
    }));
    // Reset to first page on new search
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
    
    // Reset to first page on filter change
    setPagination(prev => ({
      ...prev,
      current: 1
    }));
    
    // Trigger a refetch with the new filters
    fetchLicenses();
  };

  const getStatusTag = (status) => {
    const statusMap = {
      [LICENSE_STATUS.ACTIVE]: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: 'Active'
      },
      [LICENSE_STATUS.REVOKED]: {
        color: 'error',
        icon: <StopOutlined />,
        text: 'Revoked'
      },
      [LICENSE_STATUS.EXPIRED]: {
        color: 'error',
        icon: <StopOutlined />,
        text: 'Expired'
      },
      [LICENSE_STATUS.PENDING]: {
        color: 'warning',
        icon: <WarningOutlined />,
        text: 'Pending'
      },
      [LICENSE_STATUS.SUSPENDED]: {
        color: 'default',
        icon: <StopOutlined />,
        text: 'Suspended'
      }
    };

    const statusInfo = statusMap[status] || { color: 'default', text: status };
    
    return (
      <Tag 
        color={statusInfo.color} 
        icon={statusInfo.icon}
        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        {statusInfo.text}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'License Key',
      dataIndex: 'license_key',
      key: 'license_key',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/licenses/${record.id}`)}
          style={{ padding: 0, height: 'auto' }}
        >
          <code>{text}</code>
        </Button>
      ),
    },
    {
      title: 'License Type',
      dataIndex: 'license_type',
      key: 'license_type',
      render: (type) => (
        <Tag color={type === LICENSE_TYPES.SUBSCRIPTION ? 'blue' : 'purple'}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Tag>
      ),
      filters: [
        { text: 'Subscription', value: LICENSE_TYPES.SUBSCRIPTION },
        { text: 'Perpetual', value: LICENSE_TYPES.PERPETUAL },
      ],
      onFilter: (value, record) => record.license_type === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
      filters: [
        { text: 'Active', value: LICENSE_STATUS.ACTIVE },
        { text: 'Expired', value: LICENSE_STATUS.EXPIRED },
        { text: 'Revoked', value: LICENSE_STATUS.REVOKED },
        { text: 'Pending', value: LICENSE_STATUS.PENDING },
        { text: 'Suspended', value: LICENSE_STATUS.SUSPENDED },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => <CustomerCell customerLicenses={record.customer_licenses} />
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.expiry_date) - new Date(b.expiry_date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button 
              type="link" 
              icon={<EditOutlined />}
              className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-30-px h-30-px d-flex justify-content-center align-items-center rounded-circle"
              onClick={() => navigate(`/admin/licenses/${record.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="button"
              icon={<DeleteOutlined />}
              className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-30-px h-30-px d-flex justify-content-center align-items-center rounded-circle"
              onClick={() => {
                setLicenseToDelete(record.id);
                setShowDeleteModal(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="license-list">
      <Card 
        title="License Management"
        extra={
          user.role !== 'USER' && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/licenses/create')}
          >
            New License
          </Button>
          )
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Search
              placeholder="Search licenses..."
              allowClear
              enterButton={<SearchOutlined style={{ paddingTop: '8px' }} />}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by status"
              allowClear
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value={LICENSE_STATUS.ACTIVE}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  Active
                </Space>
              </Option>
              <Option value={LICENSE_STATUS.EXPIRED}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <WarningOutlined style={{ color: '#faad14' }} />
                  Expired
                </Space>
              </Option>
              <Option value={LICENSE_STATUS.REVOKED}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <StopOutlined style={{ color: '#ff4d4f' }} />
                  Revoked
                </Space>
              </Option>
              <Option value={LICENSE_STATUS.PENDING}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <ClockCircleOutlined style={{ color: '#1890ff' }} />
                  Pending
                </Space>
              </Option>
              <Option value={LICENSE_STATUS.SUSPENDED}>
                <Space style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <PauseCircleOutlined style={{ color: '#faad14' }} />
                  Suspended
                </Space>
              </Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by type"
              allowClear
              onChange={(value) => handleFilterChange('license_type', value)}
            >
              <Option value={LICENSE_TYPES.SUBSCRIPTION}>Subscription</Option>
              <Option value={LICENSE_TYPES.PERPETUAL}>Perpetual</Option>
            </Select>
          </Col>
        </Row>

        <Modal
          title="Delete License"
          open={showDeleteModal}
          onOk={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
        >
          <p>Are you sure you want to delete this license? This action cannot be undone.</p>
        </Modal>

        <Table
          columns={columns}
          dataSource={licenses}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} licenses`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default LicenseList;
